import * as graphics from "@client/app/graphics";
import {calculateDOTS, calculateWilks} from "@client/calculations";
import {AsyncButton} from "@client/components/Button";
import {CalculationModal} from "@client/components/CalculationModal";
import {getProfile} from "@client/features/orm";
import {copyTrackedCalculatorLink} from "@client/features/sharing";
import {useQueryString} from "@client/hooks";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {
    kgPerLb,
    levelLabel,
    levels,
    oneRepMax,
    percentiles,
    ranking,
    standards,
    strengthLifts,
    strengthMethod,
    thresholds,
} from "@client/shared/strength";
import {useSiteContext} from "@client/site/context";
import {useAppSelector} from "@client/store";
import * as utils from "@client/utils";
import {
    ArrowUpTrayIcon,
    CheckIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import {classNames, reverse} from "@reactivated";
import {produce} from "immer";
import * as React from "react";
import {match} from "ts-pattern";

type Unit = "kg" | "lb";
type WeightInput = {input: string; kilograms: DecimalValue | null};
// bottomSection renders outside <main>, so the footer carries the theme too.
const CALCULATOR_THEME =
    "font-[-apple-system,system-ui,sans-serif] font-light text-[#181a1b] dark:text-slate-100 [&_button]:cursor-pointer [&_button:disabled]:cursor-default [&_button:disabled]:opacity-40 [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-offset-[5px] [&_button:focus-visible]:outline-[#ef8165]";

type Screen =
    | {kind: "calculator"}
    | {kind: "picker"; query: string}
    | {kind: "standards"};

function weightInput(input: string, unit: Unit): WeightInput {
    const value = Number(input);
    return {
        input,
        kilograms:
            input.trim() !== "" && Number.isFinite(value) && value >= 0
                ? String(value * (unit === "lb" ? kgPerLb : 1))
                : null,
    };
}

function NumberField(props: {
    label: string;
    value: string;
    unit: string;
    min: number;
    max: number;
    step?: string;
    disabled?: boolean;
    error: string | null;
    onCommit: (value: string) => void;
    onChange: (value: string) => void;
}) {
    const initialValue = React.useRef(props.value);
    const [showError, setShowError] = React.useState(false);
    React.useEffect(() => {
        if (props.error == null || props.value.trim() === "") setShowError(false);
    }, [props.error, props.value]);
    return (
        <label className="flex min-w-0 flex-col gap-1.5 border-b border-[#d8d8d8] pb-2 dark:border-slate-700">
            <span className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400">
                {props.label}
            </span>
            <div className="flex items-baseline gap-1.5">
                <input
                    className="w-full min-w-0 [appearance:textfield] rounded-none border-0 bg-transparent p-0 text-[30px] leading-[1.2] font-light text-[#181a1b] shadow-none dark:text-slate-100 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
                    aria-label={props.label}
                    aria-invalid={showError && props.error != null}
                    type="number"
                    inputMode={props.step === "1" ? "numeric" : "decimal"}
                    value={props.disabled === true ? "" : props.value}
                    disabled={props.disabled}
                    placeholder="—"
                    min={props.min}
                    max={props.max}
                    step={props.step ?? "any"}
                    onChange={(event) => props.onChange(event.target.value)}
                    onFocus={(event) => {
                        initialValue.current = event.target.value;
                        event.target.select();
                    }}
                    onBlur={(event) => {
                        setShowError(
                            event.target.value.trim() !== "" && props.error != null,
                        );
                        if (event.target.value !== initialValue.current)
                            props.onCommit(event.target.value);
                    }}
                />
                <span className="text-[15px] text-[#777] dark:text-slate-400">
                    {props.unit}
                </span>
            </div>
            {showError && props.error != null && (
                <p
                    className="text-[13px] leading-[1.4] text-red-600 dark:text-red-300"
                    role="alert"
                >
                    {props.error}
                </p>
            )}
        </label>
    );
}

function Choices<T extends string>(props: {
    label: string;
    value: T;
    options: {value: T; label: string}[];
    onChange: (value: T) => void;
}) {
    return (
        <div className="flex gap-4" role="group" aria-label={props.label}>
            {props.options.map((option) => (
                <button
                    type="button"
                    key={option.value}
                    className="min-h-9 border-b-[1.5px] border-transparent pt-0.5 pb-1 text-[15px] text-[#777] aria-pressed:border-[#181a1b] aria-pressed:text-[#181a1b] dark:text-slate-400 dark:aria-pressed:border-slate-100 dark:aria-pressed:text-slate-100"
                    aria-pressed={props.value === option.value}
                    onClick={() => props.onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

export const Strength = router.Strength.useConditions((props) => {
    const site = useSiteContext();
    const profile = useAppSelector(getProfile) ?? site.profile;
    const origin = new URL(site.request.url).origin;
    const exercises = React.useMemo(
        () =>
            props.resolved.strength.exercises
                .filter(
                    (exercise) =>
                        exercise.slug != null &&
                        Object.hasOwn(strengthLifts, exercise.slug),
                )
                .sort(
                    (a, b) =>
                        Object.keys(strengthLifts).indexOf(a.slug ?? "") -
                        Object.keys(strengthLifts).indexOf(b.slug ?? ""),
                ),
        [props.resolved.strength.exercises],
    );
    const [screen, setScreen] = React.useState<Screen>({kind: "calculator"});
    const [showMethod, setShowMethod] = React.useState(false);
    const [comparison, setComparison] = React.useState<
        "you" | "bodyweight" | "age" | "community"
    >("you");
    const [state, setState] = React.useState(() => {
        const query = props.location.query;
        const unit: Unit =
            query.get("unit") === "kg"
                ? "kg"
                : query.get("unit") === "lb"
                  ? "lb"
                  : props.resolved.strength.unit;
        const selected = exercises.find(
            (exercise) => exercise.slug === query.get("exercise"),
        );
        const bodyweight = weightInput(
            (selected != null ? query.get("bodyweight") : null) ??
                (props.resolved.strength.bodyweight != null
                    ? String(
                          Math.round(
                              (Number(props.resolved.strength.bodyweight) /
                                  (unit === "lb" ? kgPerLb : 1)) *
                                  10,
                          ) / 10,
                      )
                    : ""),
            unit,
        );
        if (
            (selected == null || query.get("bodyweight") == null) &&
            props.resolved.strength.bodyweight != null
        )
            bodyweight.kilograms = props.resolved.strength.bodyweight;
        return {
            exercise: selected?.uuid ?? null,
            strict:
                selected != null
                    ? query.get("strict") === "1"
                        ? true
                        : query.get("strict") === "0"
                          ? false
                          : null
                    : null,
            unit,
            gender:
                query.get("gender") === "female"
                    ? ("female" as const)
                    : query.get("gender") === "male"
                      ? ("male" as const)
                      : props.resolved.strength.gender,
            bodyweight,
            age:
                (selected != null ? query.get("age") : null) ??
                String(props.resolved.strength.age ?? ""),
            lift: weightInput(selected != null ? (query.get("lift") ?? "") : "", unit),
            reps: selected != null ? (query.get("reps") ?? "") : "",
        };
    });
    const update = <K extends keyof typeof state>(key: K, value: (typeof state)[K]) =>
        setState(
            produce((draft) => {
                draft[key] = value;
            }),
        );
    const exercise = exercises.find((item) => item.uuid === state.exercise);
    const formAnswered = state.strict != null;
    const standard = exercise?.slug != null ? standards[exercise.slug] : null;
    const table = standard?.[state.gender];
    const bodyweight = Number(state.bodyweight.kilograms);
    const lifted = Number(state.lift.kilograms);
    const reps = Number(state.reps);
    const age = Number(state.age);
    const multiplier = state.unit === "lb" ? kgPerLb : 1;
    const errors = {
        bodyweight:
            state.bodyweight.kilograms == null || bodyweight <= 0
                ? "Enter your bodyweight."
                : table != null &&
                    (bodyweight < table.bodyweight[0][0] ||
                        bodyweight > table.bodyweight[table.bodyweight.length - 1][0])
                  ? `Benchmarks cover ${Math.ceil(table.bodyweight[0][0] / multiplier)}–${Math.floor(table.bodyweight[table.bodyweight.length - 1][0] / multiplier)} ${state.unit}.`
                  : null,
        age:
            !Number.isInteger(age) || age < 15 || age > 90
                ? "Enter an age from 15 to 90."
                : null,
        lift:
            state.lift.kilograms == null || lifted <= 0 || lifted > 2000
                ? "Enter a lifted weight between 0 and 2,000 kg."
                : null,
        reps:
            !Number.isInteger(reps) || reps < 1 || reps > 30
                ? "Enter 1–30 whole repetitions."
                : null,
    };
    const error =
        errors.bodyweight ??
        errors.age ??
        (screen.kind === "standards" ? null : (errors.lift ?? errors.reps));
    const maximum = oneRepMax(lifted, reps);
    const values =
        table != null && error == null
            ? thresholds(table, bodyweight, age, false)
            : null;
    const result = values != null && formAnswered ? ranking(maximum, values) : null;
    const shown = result != null && values != null ? {result, values} : null;
    const display = shown ?? {
        result: {level: 0, percentile: 0},
        values: [0, 0, 0, 0, 0],
    };
    const formatWeight = (value: number) =>
        Math.round(value / multiplier).toLocaleString("en-US");
    const ordinalSuffix = (value: number) =>
        value % 100 >= 11 && value % 100 <= 13
            ? "th"
            : match(value % 10)
                  .with(1, () => "st")
                  .with(2, () => "nd")
                  .with(3, () => "rd")
                  .otherwise(() => "th");
    const switchUnit = (unit: Unit) =>
        setState(
            produce((draft) => {
                if (unit === draft.unit) return;
                draft.unit = unit;
                if (draft.bodyweight.kilograms != null)
                    draft.bodyweight.input = String(
                        Math.round(
                            (Number(draft.bodyweight.kilograms) /
                                (unit === "lb" ? kgPerLb : 1)) *
                                10,
                        ) / 10,
                    );
                if (draft.lift.kilograms != null)
                    draft.lift.input = String(
                        Math.round(
                            Number(draft.lift.kilograms) /
                                (unit === "lb" ? kgPerLb : 1),
                        ),
                    );
            }),
        );
    const queryString =
        state.exercise == null
            ? ""
            : new URLSearchParams({
                  exercise: exercise?.slug ?? "",
                  gender: state.gender,
                  unit: state.unit,
                  bodyweight:
                      state.bodyweight.kilograms == null
                          ? state.bodyweight.input
                          : String(Number(state.bodyweight.kilograms) / multiplier),
                  lift:
                      state.lift.kilograms == null
                          ? state.lift.input
                          : String(Number(state.lift.kilograms) / multiplier),
                  age: state.age,
                  reps: state.reps,
                  ...(state.strict != null ? {strict: state.strict ? "1" : "0"} : {}),
              }).toString();
    useQueryString(queryString);
    React.useEffect(() => {
        if (props.location.query.has("exercise") && exercise?.slug != null) {
            void analytics.track("STRENGTH_URL_RESTORED", {
                strength_exercise: exercise.slug,
                strength_strict: state.strict,
            });
        }
    }, []);
    const calculationEvent = React.useMemo(
        () =>
            result == null ||
            lifted <= 0 ||
            lifted > 2000 ||
            !Number.isInteger(reps) ||
            reps < 1 ||
            reps > 30
                ? null
                : {
                      strength_exercise: exercise?.slug ?? "",
                      strength_strict: state.strict,
                      strength_gender: state.gender,
                      strength_unit: state.unit,
                      strength_bodyweight_kg: bodyweight,
                      strength_lift_kg: lifted,
                      strength_reps: reps,
                      strength_age: age,
                      strength_estimated_max_kg: maximum,
                      strength_level: levelLabel(result.level),
                      strength_percentile: result.percentile,
                  },
        [
            exercise?.slug,
            state.gender,
            state.strict,
            state.unit,
            bodyweight,
            lifted,
            reps,
            age,
            maximum,
            result?.level,
            result?.percentile,
        ],
    );
    React.useEffect(() => {
        if (calculationEvent == null) return;
        const timeout = window.setTimeout(() => {
            void analytics.track("STRENGTH_CALCULATION_COMPLETE", calculationEvent);
        }, 500);
        return () => window.clearTimeout(timeout);
    }, [calculationEvent]);
    const share = async () => {
        const url = new URL(
            reverse("strength"),
            utils.getBaseURLWithOriginForCapacitor(),
        );
        url.search = queryString;
        try {
            const code = await copyTrackedCalculatorLink(url.href, "STRENGTH");
            props.notify("Link copied", {type: "success"});
            if (calculationEvent != null)
                void analytics.track("STRENGTH_SHARED_CALCULATION", {
                    ...calculationEvent,
                    shared_tracking_code: code,
                });
        } catch {
            props.notify("Could not copy the link. Please try again.", {
                type: "error",
            });
        }
    };
    const label =
        exercise != null ? (exercise.short_name ?? exercise.name) : "Choose a lift";
    const footer =
        screen.kind === "calculator" ? (
            <footer
                className={` ${CALCULATOR_THEME} flex items-center justify-between gap-4`}
            >
                <button
                    type="button"
                    className="min-h-11 text-left text-[15px] font-normal underline underline-offset-[3px]"
                    onClick={() => {
                        void analytics.track("STRENGTH_SHOWED_FORMULA", {
                            strength_strict: state.strict,
                        });
                        setShowMethod(true);
                    }}
                >
                    How this is calculated
                </button>
                <AsyncButton
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#d8d8d8] px-5 text-[15px] hover:bg-[#f0eee8] dark:border-slate-700 dark:hover:bg-[#172033]"
                    onClick={share}
                    disabled={result == null}
                >
                    <ArrowUpTrayIcon
                        className="size-4"
                        strokeWidth={1.25}
                        aria-hidden="true"
                    />
                    Share
                </AsyncButton>
            </footer>
        ) : undefined;
    return {
        options: {
            title: "Strength Standards",
            respectDarkMode: true,
            bottomSection: footer,
            back:
                screen.kind === "calculator"
                    ? undefined
                    : {
                          title: "Back",
                          onClick: () => setScreen({kind: "calculator"}),
                      },
            titleSection:
                screen.kind !== "calculator" ? (
                    <div className="h-7" />
                ) : (
                    <div className="flex w-full items-center justify-center gap-3">
                        <graphics.Logo className="h-5 w-auto shrink-0 dark:invert" />
                        <span className="text-gray-300 dark:text-slate-600">|</span>
                        <span className="text-xl font-light text-gray-600 dark:text-slate-300">
                            Strength
                        </span>
                    </div>
                ),
            scene: {
                kind: "SIMPLE",
                nav: profile != null ? "menu" : "none",
                width: "column",
            },
        },
        actions: [],
        view: (
            <>
                <link rel="canonical" href={`${origin}${reverse("strength")}`} />
                <meta
                    name="description"
                    content="Strength standards by exercise, bodyweight, age and gender. Calculate your estimated one-rep max and strength level."
                />
                <meta
                    property="og:title"
                    content="Strength Standards | Joy Health Tracker"
                />
                <meta
                    property="og:description"
                    content="Compare lifts by bodyweight, age and gender."
                />
                <meta
                    property="og:image"
                    content={`${origin}${reverse("strength_open_graph")}${queryString === "" ? "" : `?${queryString}`}`}
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`${origin}${reverse("strength")}`} />
                <meta name="twitter:card" content="summary_large_image" />
                <main
                    data-strength-standards
                    className={` ${CALCULATOR_THEME} mx-auto flex min-h-full w-full max-w-[440px] flex-col px-2 pt-0 sm:pt-1.5 [&_input:focus-visible]:outline-2 [&_input:focus-visible]:outline-offset-[5px] [&_input:focus-visible]:outline-[#ef8165]`}
                >
                    {match(screen)
                        .with({kind: "picker"}, ({query}) => {
                            const filtered = exercises.filter((item) =>
                                `${item.short_name ?? item.name} ${item.name} ${strengthLifts[item.slug ?? ""].group}`
                                    .toLowerCase()
                                    .includes(query.trim().toLowerCase()),
                            );
                            return (
                                <section className="flex flex-col gap-3.5 sm:gap-5">
                                    <h1 className="m-0 mt-0.5 text-[30px] leading-[1.15] font-light tracking-normal text-[#373a3c] sm:mt-1.5 dark:text-slate-300">
                                        Choose a lift
                                    </h1>
                                    <label className="flex items-center gap-2.5 border-b border-[#d8d8d8] py-3 dark:border-slate-700">
                                        <MagnifyingGlassIcon className="size-[18px] text-[#777] dark:text-slate-400" />
                                        <input
                                            className="w-full min-w-0 flex-1 rounded-none border-0 bg-transparent p-0 text-[17px] text-[#181a1b] placeholder:text-[#777] dark:text-slate-100 dark:placeholder:text-slate-400 [&::-webkit-search-cancel-button]:appearance-none"
                                            type="search"
                                            aria-label="Search lifts"
                                            placeholder="Search lifts"
                                            value={query}
                                            onChange={(event) =>
                                                setScreen({
                                                    kind: "picker",
                                                    query: event.target.value,
                                                })
                                            }
                                        />
                                        {query !== "" && (
                                            <button
                                                type="button"
                                                className="p-[3px] text-[#777] dark:text-slate-400"
                                                aria-label="Clear search"
                                                onClick={() =>
                                                    setScreen({
                                                        kind: "picker",
                                                        query: "",
                                                    })
                                                }
                                            >
                                                <XMarkIcon className="size-5" />
                                            </button>
                                        )}
                                    </label>
                                    <div className="flex flex-col">
                                        {filtered.map((item) => (
                                            <button
                                                className="flex w-full items-center justify-between gap-2 border-b border-[#d8d8d8] py-[13px] text-left text-lg/[inherit] aria-pressed:font-normal sm:py-[17px] dark:border-slate-700"
                                                key={item.uuid}
                                                type="button"
                                                aria-pressed={
                                                    state.exercise === item.uuid
                                                }
                                                onClick={() => {
                                                    void analytics.track(
                                                        "STRENGTH_SELECTED_EXERCISE",
                                                        {
                                                            strength_exercise:
                                                                item.slug ?? "",
                                                        },
                                                    );
                                                    setState(
                                                        produce((draft) => {
                                                            if (
                                                                draft.exercise !==
                                                                item.uuid
                                                            )
                                                                draft.strict = null;
                                                            draft.exercise = item.uuid;
                                                        }),
                                                    );
                                                    setScreen({kind: "calculator"});
                                                }}
                                            >
                                                <span>
                                                    {item.short_name ?? item.name}
                                                </span>
                                                <span className="inline-flex items-center gap-2 text-sm/[inherit] text-[#777] dark:text-slate-400 [&_svg]:size-4 [&_svg]:text-[#ef8165]">
                                                    {
                                                        strengthLifts[item.slug ?? ""]
                                                            .group
                                                    }
                                                    {state.exercise === item.uuid && (
                                                        <CheckIcon />
                                                    )}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                    {filtered.length === 0 && (
                                        <p className="text-sm/normal text-[#777] dark:text-slate-400">
                                            No matching lifts.
                                        </p>
                                    )}
                                </section>
                            );
                        })
                        .with({kind: "standards"}, () => (
                            <section className="flex flex-col gap-5">
                                <h1 className="m-0 text-[30px] leading-[1.15] font-light tracking-normal text-[#373a3c] dark:text-slate-300">
                                    {label} standards
                                </h1>
                                <Choices
                                    label="Compare by"
                                    value={comparison}
                                    onChange={(value) => {
                                        void analytics.track(
                                            "STRENGTH_VIEWED_STANDARDS",
                                            {
                                                strength_strict: state.strict,
                                                strength_exercise: exercise?.slug ?? "",
                                                strength_comparison: value,
                                            },
                                        );
                                        setComparison(value);
                                    }}
                                    options={[
                                        {value: "you", label: "For you"},
                                        {value: "bodyweight", label: "Weight"},
                                        {value: "age", label: "Age"},
                                        {value: "community", label: "All lifters"},
                                    ]}
                                />
                                {table != null &&
                                    (comparison === "you" ||
                                    comparison === "community" ? (
                                        <div>
                                            {(comparison === "you"
                                                ? (values ?? [])
                                                : table.community
                                            ).map((value, i) => (
                                                <div
                                                    key={levels[i]}
                                                    className="flex items-center justify-between border-b border-[#d8d8d8] py-4 text-[17px] dark:border-slate-700"
                                                >
                                                    <span>
                                                        {levels[i]}
                                                        <small className="mt-1 block text-[13px] text-[#777] dark:text-slate-400">
                                                            {percentiles[i]}th
                                                            percentile
                                                        </small>
                                                    </span>
                                                    <strong className="text-2xl leading-[1.2] font-light whitespace-nowrap">
                                                        {formatWeight(value)}{" "}
                                                        <span className="text-sm/[inherit] text-[#777] dark:text-slate-400">
                                                            {state.unit}
                                                        </span>
                                                    </strong>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <table className="w-full border-collapse text-right text-sm/[inherit] [&_td]:border-b [&_td]:border-[#d8d8d8] [&_td]:px-0.5 [&_td]:py-3 dark:[&_td]:border-slate-700 [&_th]:border-b [&_th]:border-[#d8d8d8] [&_th]:px-0.5 [&_th]:py-3 [&_th]:font-normal dark:[&_th]:border-slate-700 [&_th:first-child]:text-left">
                                            <caption className="pb-3 text-left text-sm/[inherit] text-[#777] dark:text-slate-400">
                                                {state.gender === "male"
                                                    ? "Male"
                                                    : "Female"}{" "}
                                                · {state.unit}
                                            </caption>
                                            <thead>
                                                <tr>
                                                    <th>
                                                        {comparison === "age"
                                                            ? "Age"
                                                            : state.unit}
                                                    </th>
                                                    {[
                                                        "Beg.",
                                                        "Nov.",
                                                        "Int.",
                                                        "Adv.",
                                                        "Elite",
                                                    ].map((level) => (
                                                        <th key={level}>{level}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {table[comparison].map((row) => (
                                                    <tr key={row[0]}>
                                                        <th>
                                                            {comparison === "age"
                                                                ? row[0]
                                                                : formatWeight(row[0])}
                                                        </th>
                                                        {row
                                                            .slice(1)
                                                            .map((value, i) => (
                                                                <td key={i}>
                                                                    {formatWeight(
                                                                        value,
                                                                    )}
                                                                </td>
                                                            ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ))}
                                <p className="text-sm/normal text-[#777] dark:text-slate-400">
                                    {comparison === "you"
                                        ? `For ${state.bodyweight.input} ${state.unit}, age ${state.age}.`
                                        : comparison === "age"
                                          ? "Age benchmarks across all bodyweights."
                                          : comparison === "bodyweight"
                                            ? "Bodyweight benchmarks across all ages."
                                            : "Benchmarks across all ages and bodyweights."}
                                </p>
                                <button
                                    className="min-h-11 text-left text-[15px] font-normal underline underline-offset-[3px]"
                                    type="button"
                                    onClick={() => {
                                        void analytics.track(
                                            "STRENGTH_SHOWED_FORMULA",
                                            {strength_strict: state.strict},
                                        );
                                        setShowMethod(true);
                                    }}
                                >
                                    How this is calculated
                                </button>
                            </section>
                        ))
                        .with({kind: "calculator"}, () => (
                            <section className="flex flex-1 flex-col justify-between gap-3.5 sm:flex-none sm:justify-start sm:gap-[26px]">
                                <div data-strength-lift>
                                    <button
                                        type="button"
                                        className="flex w-full items-baseline justify-between gap-4 border-b border-[#d8d8d8] pt-0.5 pb-3 text-left sm:pt-1.5 sm:pb-[15px] dark:border-slate-700"
                                        aria-label={
                                            exercise == null
                                                ? "Select an exercise"
                                                : `Change exercise: ${label}`
                                        }
                                        onClick={() =>
                                            setScreen({kind: "picker", query: ""})
                                        }
                                    >
                                        <h1 className="m-0 text-[30px] leading-[1.15] font-light tracking-normal text-[#373a3c] dark:text-slate-300">
                                            {label}
                                        </h1>
                                        <span className="flex shrink-0 items-center gap-1.5 text-[15px] text-[#777] dark:text-slate-400 [&_svg]:size-[13px]">
                                            {exercise == null ? "Select" : "Change"}{" "}
                                            <ChevronRightIcon />
                                        </span>
                                    </button>
                                    <fieldset
                                        className="h-29 pt-3"
                                        aria-describedby="strict-instructions"
                                    >
                                        <legend className="sr-only">Form</legend>
                                        <div className="flex flex-col gap-1.5">
                                            <span
                                                aria-hidden="true"
                                                className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400"
                                            >
                                                Form
                                            </span>
                                            <div className="grid grid-cols-2 gap-x-6">
                                                {[
                                                    {value: true, label: "Strict"},
                                                    {
                                                        value: false,
                                                        label: "Not sure",
                                                    },
                                                ].map((option) => (
                                                    <label
                                                        key={option.label}
                                                        className="cursor-pointer"
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="strict-form"
                                                            value={
                                                                option.value ? "1" : "0"
                                                            }
                                                            checked={
                                                                state.strict ===
                                                                option.value
                                                            }
                                                            disabled={exercise == null}
                                                            className="peer sr-only"
                                                            onChange={() => {
                                                                update(
                                                                    "strict",
                                                                    option.value,
                                                                );
                                                                void analytics.track(
                                                                    "STRENGTH_CHANGED_STRICT",
                                                                    {
                                                                        strength_exercise:
                                                                            exercise?.slug ??
                                                                            "",
                                                                        strength_strict:
                                                                            option.value,
                                                                    },
                                                                );
                                                            }}
                                                        />
                                                        <span className="flex min-h-9 items-center justify-center border-b-[1.5px] border-[#d8d8d8] text-[15px] text-[#777] peer-checked:border-[#181a1b] peer-checked:text-[#181a1b] peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-disabled:opacity-40 dark:border-slate-700 dark:text-slate-400 dark:peer-checked:border-slate-100 dark:peer-checked:text-slate-100">
                                                            {option.label}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                        <p
                                            id="strict-instructions"
                                            className="mt-2 line-clamp-2 text-xs/normal text-pretty whitespace-pre-line text-[#777] dark:text-slate-400"
                                        >
                                            {exercise?.strict_instructions ??
                                                "Each lift has a strict standard. Pick one to see it."}
                                        </p>
                                    </fieldset>
                                </div>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:gap-y-5">
                                    <NumberField
                                        disabled={exercise == null}
                                        onCommit={(value) =>
                                            void analytics.track(
                                                "STRENGTH_CHANGED_INPUT",
                                                {
                                                    strength_exercise:
                                                        exercise?.slug ?? "",
                                                    strength_strict: state.strict,
                                                    strength_field: "lift",
                                                    strength_value: value,
                                                },
                                            )
                                        }
                                        error={errors.lift}
                                        label="Lifted"
                                        value={state.lift.input}
                                        unit={state.unit}
                                        min={1}
                                        max={2000 / multiplier}
                                        onChange={(value) =>
                                            update(
                                                "lift",
                                                weightInput(value, state.unit),
                                            )
                                        }
                                    />
                                    <NumberField
                                        disabled={exercise == null}
                                        onCommit={(value) =>
                                            void analytics.track(
                                                "STRENGTH_CHANGED_INPUT",
                                                {
                                                    strength_exercise:
                                                        exercise?.slug ?? "",
                                                    strength_strict: state.strict,
                                                    strength_field: "reps",
                                                    strength_value: value,
                                                },
                                            )
                                        }
                                        error={errors.reps}
                                        label="Reps"
                                        value={state.reps}
                                        unit="reps"
                                        min={1}
                                        max={30}
                                        step="1"
                                        onChange={(value) => update("reps", value)}
                                    />
                                    <NumberField
                                        disabled={exercise == null}
                                        onCommit={(value) =>
                                            void analytics.track(
                                                "STRENGTH_CHANGED_INPUT",
                                                {
                                                    strength_exercise:
                                                        exercise?.slug ?? "",
                                                    strength_strict: state.strict,
                                                    strength_field: "bodyweight",
                                                    strength_value: value,
                                                },
                                            )
                                        }
                                        error={errors.bodyweight}
                                        label="Bodyweight"
                                        value={state.bodyweight.input}
                                        unit={state.unit}
                                        min={1}
                                        max={1000}
                                        onChange={(value) =>
                                            update(
                                                "bodyweight",
                                                weightInput(value, state.unit),
                                            )
                                        }
                                    />
                                    <NumberField
                                        disabled={exercise == null}
                                        onCommit={(value) =>
                                            void analytics.track(
                                                "STRENGTH_CHANGED_INPUT",
                                                {
                                                    strength_exercise:
                                                        exercise?.slug ?? "",
                                                    strength_strict: state.strict,
                                                    strength_field: "age",
                                                    strength_value: value,
                                                },
                                            )
                                        }
                                        error={errors.age}
                                        label="Age"
                                        value={state.age}
                                        unit="years"
                                        min={15}
                                        max={90}
                                        step="1"
                                        onChange={(value) => update("age", value)}
                                    />
                                </div>
                                <div className="flex justify-between">
                                    <Choices
                                        label="Gender"
                                        value={state.gender}
                                        onChange={(value) => {
                                            void analytics.track(
                                                "STRENGTH_CHANGED_GENDER",
                                                {
                                                    strength_gender: value,
                                                    strength_strict: state.strict,
                                                },
                                            );
                                            update("gender", value);
                                        }}
                                        options={[
                                            {value: "male", label: "Male"},
                                            {value: "female", label: "Female"},
                                        ]}
                                    />
                                    <Choices
                                        label="Weight unit"
                                        value={state.unit}
                                        onChange={(value) => {
                                            void analytics.track(
                                                "STRENGTH_CHANGED_UNIT",
                                                {
                                                    strength_unit: value,
                                                    strength_strict: state.strict,
                                                },
                                            );
                                            switchUnit(value);
                                        }}
                                        options={[
                                            {value: "kg", label: "kg"},
                                            {value: "lb", label: "lb"},
                                        ]}
                                    />
                                </div>
                                <div
                                    className="grid *:[grid-area:1/1]"
                                    aria-live="polite"
                                    aria-atomic="true"
                                >
                                    <p
                                        className={classNames(
                                            "text-sm/normal text-[#777] dark:text-slate-400",
                                            exercise != null && "invisible",
                                        )}
                                    >
                                        Select an exercise to see your strength level.
                                    </p>
                                    <p
                                        className={classNames(
                                            "text-sm/normal text-[#777] dark:text-slate-400",
                                            (exercise == null || formAnswered) &&
                                                "invisible",
                                        )}
                                    >
                                        Choose{" "}
                                        <span className="font-medium">Strict</span> or{" "}
                                        <span className="font-medium">Not sure</span> to
                                        see your result.
                                    </p>
                                    <div
                                        className={classNames(
                                            "relative",
                                            shown == null && "invisible",
                                        )}
                                    >
                                        <div className="flex flex-col gap-2">
                                            <span className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400">
                                                Your level
                                            </span>
                                            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 sm:gap-y-2">
                                                <h2
                                                    data-level={display.result.level}
                                                    className="m-0 text-[40px] leading-none font-extralight tracking-[-0.01em] data-[level=-1]:text-[40px] sm:text-[48px]"
                                                >
                                                    {levelLabel(display.result.level)}
                                                </h2>
                                                <p className="m-0 text-[17px] whitespace-nowrap text-[#373a3c] dark:text-slate-300">
                                                    {display.result.percentile >= 95
                                                        ? "95+"
                                                        : display.result.percentile}
                                                    <sup className="text-[10px]">
                                                        {ordinalSuffix(
                                                            display.result.percentile,
                                                        )}
                                                    </sup>{" "}
                                                    percentile
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="absolute -top-1 right-0 flex min-h-[26px] items-center gap-[3px] text-sm/[inherit] text-[#777] dark:text-slate-400 [&_svg]:size-[13px]"
                                            onClick={() => {
                                                void analytics.track(
                                                    "STRENGTH_VIEWED_STANDARDS",
                                                    {
                                                        strength_exercise:
                                                            exercise?.slug ?? "",
                                                        strength_strict: state.strict,
                                                        strength_comparison: comparison,
                                                    },
                                                );
                                                setScreen({kind: "standards"});
                                            }}
                                        >
                                            View standards <ChevronRightIcon />
                                        </button>
                                    </div>
                                </div>
                                <div
                                    className={classNames(
                                        "grid grid-cols-5 gap-[3px]",
                                        shown == null && "invisible",
                                    )}
                                >
                                    {display.values.map((value, i) => {
                                        const next =
                                            display.values[i + 1] ?? value * 1.25;
                                        const fill = Math.min(
                                            1,
                                            Math.max(
                                                0,
                                                (maximum - value) / (next - value),
                                            ),
                                        );
                                        return (
                                            <div
                                                className="group"
                                                data-current={
                                                    i === display.result.level
                                                }
                                                key={levels[i]}
                                                title={`${levels[i]}: ${formatWeight(value)} ${state.unit}`}
                                            >
                                                <div className="h-2 overflow-hidden rounded-[2px] bg-[#e7e5df] dark:bg-slate-800">
                                                    <span
                                                        className="block h-full bg-[#181a1b] transition-[width] duration-200 ease-[ease] group-data-[current=true]:bg-[#ef8165] motion-reduce:transition-none dark:bg-slate-100 dark:group-data-[current=true]:bg-[#ef8165]"
                                                        style={{
                                                            width: `${fill * 100}%`,
                                                        }}
                                                    />
                                                </div>
                                                <span className="mt-2 block text-sm/[inherit] text-[#777] group-data-[current=true]:font-normal group-data-[current=true]:text-[#181a1b] dark:text-slate-400 dark:group-data-[current=true]:text-slate-100">
                                                    {formatWeight(value)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div
                                    className={classNames(
                                        "grid grid-cols-4 gap-3 border-t border-[#d8d8d8] pt-3 sm:pt-4 dark:border-slate-700",
                                        shown == null && "invisible",
                                    )}
                                >
                                    <div className="flex min-w-0 flex-col gap-1.5">
                                        <span className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400">
                                            {reps === 1 ? "1RM" : "Est. 1RM"}
                                        </span>
                                        <strong className="text-2xl leading-[1.2] font-light whitespace-nowrap">
                                            {formatWeight(maximum)}{" "}
                                            <small className="text-sm/[inherit] text-[#777] dark:text-slate-400">
                                                {state.unit}
                                            </small>
                                        </strong>
                                    </div>
                                    <div className="flex min-w-0 flex-col gap-1.5">
                                        <span className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400">
                                            Ratio
                                        </span>
                                        <strong className="text-2xl leading-[1.2] font-light whitespace-nowrap">
                                            {(bodyweight > 0
                                                ? maximum / bodyweight
                                                : 0
                                            ).toFixed(1)}
                                            ×
                                        </strong>
                                    </div>
                                    <div className="flex min-w-0 flex-col gap-1.5">
                                        <span className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400">
                                            DOTS
                                        </span>
                                        <strong className="text-2xl leading-[1.2] font-light whitespace-nowrap">
                                            {calculateDOTS(
                                                state.bodyweight.kilograms,
                                                String(maximum),
                                                state.gender === "female" ? "f" : "m",
                                            )?.toFixed(0)}
                                        </strong>
                                    </div>
                                    <div className="flex min-w-0 flex-col gap-1.5">
                                        <span className="text-[13px] leading-[1.3] tracking-wider text-[#777] uppercase dark:text-slate-400">
                                            Wilks
                                        </span>
                                        <strong className="text-2xl leading-[1.2] font-light whitespace-nowrap">
                                            {calculateWilks(
                                                state.bodyweight.kilograms,
                                                String(maximum),
                                                state.gender === "female" ? "f" : "m",
                                            )?.toFixed(0)}
                                        </strong>
                                    </div>
                                </div>
                            </section>
                        ))
                        .exhaustive()}
                </main>
                <CalculationModal
                    open={showMethod}
                    content={strengthMethod}
                    onClose={() => setShowMethod(false)}
                />
            </>
        ),
    };
});
